import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CreditCard, Calendar, FileText, Bell, ArrowRight, Home, Search, CheckCircle2, Clock, HelpCircle, PartyPopper, X, HandHeart, Sparkles } from "lucide-react";
import { formatLocalDate } from "@/lib/date";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LaborDayEventCard from "@/components/LaborDayEventCard";
import LaborDayRsvpForm from "@/components/LaborDayRsvpForm";

export default function PortalDashboard() {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [myProperty, setMyProperty] = useState(undefined); // undefined=loading, null=none, object=linked
  const [sp, setSp] = useSearchParams();
  const paidPollRef = useRef({ tries: 0, timer: null });
  const [justPaid, setJustPaid] = useState(false);

  const loadAll = async () => {
    try {
      const [p, b, a, mp] = await Promise.all([
        api.get("/payments/history"),
        api.get("/bookings/my").catch(() => ({ data: [] })),
        api.get("/announcements"),
        api.get("/portal/my-property"),
      ]);
      setPayments(p.data);
      setBookings(b.data);
      setAnnouncements(a.data.slice(0, 3));
      setMyProperty(mp.data);
      return p.data;
    } catch (e) { console.error("Portal load failed:", e); return null; }
  };
  useEffect(() => { loadAll(); }, []);

  // Auto-refresh after returning from Stripe checkout. If the URL carries ?paid=1
  // (added by PaymentSuccessPage when it redirects), we (a) toast the member,
  // (b) drop the query param so a refresh doesn't repeat it, and (c) poll payment
  // history for 30 s in case the webhook is still catching up — so the freshly
  // paid transaction transitions from "pending" → "paid" without a manual reload.
  useEffect(() => {
    if (sp.get("paid") !== "1") return;
    toast.success("Payment recorded — your account is up to date");
    setJustPaid(true);
    setSp(new URLSearchParams(""), { replace: true });
    const pollForConfirmed = async () => {
      const data = await loadAll();
      const hasNewPaid = (data || []).some((p) => p.payment_status === "paid" && Date.now() - new Date(p.created_at).getTime() < 5 * 60 * 1000);
      const st = paidPollRef.current;
      st.tries += 1;
      if (!hasNewPaid && st.tries < 15) {
        st.timer = setTimeout(pollForConfirmed, 2000);
      }
    };
    paidPollRef.current.timer = setTimeout(pollForConfirmed, 1500);
    return () => { if (paidPollRef.current.timer) clearTimeout(paidPollRef.current.timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const unlink = async () => {
    if (!window.confirm("Unlink your home from this account? You can search and link again anytime.")) return;
    try {
      await api.post("/portal/unlink-property");
      toast.success("Home unlinked");
      setMyProperty(null);
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="portal-dashboard" className="max-w-7xl mx-auto px-6 py-12">
      <div className="mb-10 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#C86650] mb-2">Portal</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#1A2421]">Welcome, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-[#5C6A64] mt-2">Your Home At a Glance.</p>
        </div>

        {/* Get Involved callout — a small pitch card, not just a button. */}
        <Link
          to="/get-involved"
          data-testid="portal-get-involved-btn"
          className="group relative block max-w-sm rounded-2xl overflow-hidden bg-gradient-to-br from-[#C86650] via-[#B4553F] to-[#8A3E2F] text-white p-5 pr-6 shadow-[0_16px_40px_rgba(200,102,80,0.35)] hover:shadow-[0_20px_50px_rgba(200,102,80,0.5)] hover:-translate-y-0.5 transition-all duration-300"
        >
          {/* Decorative floating icons */}
          <HandHeart className="absolute -right-3 -bottom-3 w-24 h-24 text-white/10 pointer-events-none" strokeWidth={1.5} />
          <Sparkles className="absolute right-4 top-3 w-4 h-4 text-white/50 animate-pulse pointer-events-none" />

          {/* Notification-style dot */}
          <span className="absolute top-3 left-3 flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
          </span>

          <div className="relative pl-5">
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#FBE7D7] mb-1">Better Together</p>
            <p className="font-serif text-xl leading-tight mb-1">Get Involved</p>
            <p className="text-xs text-[#FBE7D7] leading-snug mb-3 max-w-[220px]">
              Community Wine Downs, cookouts, holiday décor and more — pick what you love.
            </p>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium">
              Go to Get Involved
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </span>
          </div>
        </Link>
      </div>

      {/* My Home card — the star of the dashboard */}
      {myProperty === undefined ? (
        <Card className="rounded-2xl border-[#D5CFC4] mb-8">
          <CardContent className="p-8 text-[#5C6A64]">Loading your home…</CardContent>
        </Card>
      ) : myProperty === null ? (
        <FindMyHomeCard onLinked={loadAll} />
      ) : (
        <>
          {myProperty.balance === 0 && myProperty.status === "current" && <PaidUpBanner property={myProperty} justPaid={justPaid} />}
          <MyHomeCard property={myProperty} onUnlink={unlink} />
        </>
      )}

      {/* Labor Day event card + RSVP form */}
      <LaborDayEventCard variant="portal" />
      <LaborDayRsvpForm />

      {/* Quick action banner */}
      <div className="grid lg:grid-cols-3 gap-6 mb-10">
        <Card className="lg:col-span-2 rounded-2xl bg-[#2C4C3B] text-white border-0 overflow-hidden relative">
          <CardContent className="p-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#EADCC8] mb-3">Pay Dues</p>
            <p className="font-serif text-3xl mb-2">Secure Stripe checkout</p>
            <p className="text-[#EADCC8] mb-8 max-w-md">
              One-time or set up auto-pay so you never miss a due date.
            </p>
            <Link to="/portal/pay">
              <Button data-testid="dash-pay-btn" className="rounded-full bg-[#C86650] hover:bg-[#B4553F] text-white h-11 px-6">
                Pay Dues <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Home className="absolute -right-4 -bottom-4 w-40 h-40 text-white/5" />
          </CardContent>
        </Card>
        <Card className="rounded-2xl bg-white border-[#D5CFC4]">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-wider text-[#5C6A64]">Amenity requests</p>
            <p className="font-serif text-2xl text-[#1A2421] mt-1 mb-4">Book a space</p>
            <p className="text-sm text-[#5C6A64] mb-4">Clubhouse, pool, tennis court — submit a booking request.</p>
            <Link to="/amenities">
              <Button variant="outline" size="sm" className="rounded-full border-[#2C4C3B] text-[#2C4C3B]" data-testid="dash-book-btn">
                View amenities
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Quick links */}
      <div className="grid md:grid-cols-4 gap-4 mb-12">
        {[
          { to: "/portal/pay", icon: CreditCard, label: "Pay Dues", testid: "quick-pay" },
          { to: "/amenities", icon: Calendar, label: "Book Amenity", testid: "quick-book" },
          { to: "/documents", icon: FileText, label: "Documents", testid: "quick-docs" },
          { to: "/announcements", icon: Bell, label: "Announcements", testid: "quick-ann" },
        ].map((q) => (
          <Link key={q.to} to={q.to} data-testid={q.testid} className="p-6 bg-white rounded-2xl border border-[#D5CFC4] hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(44,76,59,0.08)] transition-transform duration-300">
            <q.icon className="w-5 h-5 text-[#C86650] mb-3" />
            <p className="font-medium text-[#1A2421]">{q.label}</p>
          </Link>
        ))}
      </div>

      {/* Recent announcements, payment history, and upcoming bookings */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="rounded-2xl border-[#D5CFC4]">
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <p className="font-serif text-2xl text-[#1A2421]">Your bookings</p>
              <Link to="/portal/bookings" className="text-xs text-[#C86650] underline">See all</Link>
            </div>
            {bookings.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-[#5C6A64] text-sm mb-3">No bookings yet.</p>
                <Link to="/amenities">
                  <Button size="sm" className="rounded-full bg-[#2C4C3B] hover:bg-[#233D2F]" data-testid="dash-book-cta">
                    <Calendar className="w-4 h-4 mr-2" /> Book an amenity
                  </Button>
                </Link>
              </div>
            ) : (
              <ul className="space-y-3" data-testid="dash-bookings-list">
                {(() => {
                  const todayIso = new Date().toISOString().slice(0, 10);
                  const upcoming = bookings
                    .filter((b) => (b.date || "") >= todayIso && !["rejected", "cancelled"].includes(b.status))
                    .sort((x, y) => (x.date || "").localeCompare(y.date || ""));
                  const past = bookings.filter((b) => !upcoming.includes(b)).slice(0, 3);
                  const visible = upcoming.length ? upcoming.slice(0, 5) : past;
                  return visible.map((b) => (
                    <li
                      key={b.id}
                      data-testid={`dash-booking-${b.id}`}
                      className="flex items-start gap-3 border-b border-[#D5CFC4] pb-3 last:border-0"
                    >
                      <div className="w-9 h-9 rounded-full bg-[#EADCC8] flex items-center justify-center shrink-0">
                        <Calendar className="w-4 h-4 text-[#2C4C3B]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-[#1A2421] capitalize">{b.amenity_name || b.amenity}</p>
                          <Badge
                            className={`text-[10px] capitalize ${
                              b.status === "confirmed"
                                ? "bg-[#2C4C3B] text-white"
                                : b.status === "pending"
                                ? "bg-[#EADCC8] text-[#2C4C3B]"
                                : "bg-[#5C6A64] text-white"
                            }`}
                          >
                            {b.status}
                          </Badge>
                          {b.deposit_paid && !b.deposit_refunded && (
                            <Badge className="text-[10px] bg-[#2C4C3B] text-white">Deposit paid</Badge>
                          )}
                          {b.manual_entry && (
                            <Badge variant="outline" className="text-[10px] border-[#5C6A64] text-[#5C6A64]">Added by board</Badge>
                          )}
                        </div>
                        <p className="text-xs text-[#5C6A64] mt-0.5">
                          {formatLocalDate(b.date, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
                          {b.time_slot ? ` · ${b.time_slot}` : ""}
                        </p>
                      </div>
                    </li>
                  ));
                })()}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-[#D5CFC4]">
          <CardContent className="p-6">
            <p className="font-serif text-2xl mb-4 text-[#1A2421]">Recent announcements</p>
            {announcements.length === 0 ? (
              <p className="text-[#5C6A64] text-sm">Nothing new.</p>
            ) : (
              <ul className="space-y-4">
                {announcements.map((a) => (
                  <li key={a.id} className="border-b border-[#D5CFC4] pb-3 last:border-0">
                    <p className="font-medium text-[#1A2421]">{a.title}</p>
                    <p className="text-sm text-[#5C6A64] line-clamp-2 mt-1">{a.body}</p>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-[#D5CFC4] lg:col-span-2">
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <p className="font-serif text-2xl text-[#1A2421]">Recent payments</p>
              {payments.length > 0 && (
                <Link to="/portal/history" data-testid="see-full-history" className="text-xs text-[#C86650] underline">See full history →</Link>
              )}
            </div>
            {payments.length === 0 ? (
              <p className="text-[#5C6A64] text-sm">No payments yet.</p>
            ) : (
              <ul className="space-y-3" data-testid="recent-payments-list">
                {payments.slice(0, 5).map((p) => {
                  const isCredit = p.lookup_key === "settlement_credit";
                  const statusColor = isCredit ? "bg-[#C86650] text-white"
                    : p.payment_status === "paid" ? "bg-[#2C4C3B] text-white"
                    : p.payment_status === "pending" ? "bg-[#EADCC8] text-[#2C4C3B]"
                    : "bg-[#C86650] text-white";
                  const statusLabel = isCredit ? "Credit applied" : p.payment_status;
                  const methodLabel = isCredit ? "Settlement credit"
                    : p.mode === "manual" ? (p.method || "manual").replace(/_/g, " ")
                    : p.mode === "subscription_renewal" ? "Auto-pay renewal"
                    : p.mode === "subscription" ? "Auto-pay"
                    : "Credit card";
                  return (
                    <li key={p.id} data-testid={`recent-payment-${p.id}`} className="flex justify-between items-center text-sm border-b border-[#D5CFC4] pb-3 last:border-0">
                      <div className="flex-1 min-w-0">
                        <p className="text-[#1A2421] font-medium truncate">{(p.lookup_key || "payment").replace(/_/g, " ")}</p>
                        <p className="text-xs text-[#5C6A64]">
                          {new Date(p.created_at).toLocaleDateString()} · {new Date(p.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · {methodLabel}
                        </p>
                      </div>
                      <div className="text-right ml-3">
                        <p className={`font-medium ${isCredit ? "text-[#C86650]" : "text-[#1A2421]"}`}>{isCredit ? "–" : ""}${(p.amount / 100).toFixed(2)}</p>
                        <Badge data-testid={`recent-payment-status-${p.id}`} className={`${statusColor} text-[10px] px-2 py-0.5 mt-0.5 capitalize`}>{statusLabel}</Badge>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


// ---------- Paid-up celebratory banner ----------
// Shown when the homeowner has $0 balance and is `current`. Dismissible per-session so
// it doesn't nag on every visit, but re-appears with a stronger tone the moment right
// after a payment finalizes (via ?paid=1 flag → justPaid prop).
function PaidUpBanner({ property, justPaid }) {
  const key = `paid-up-banner-dismissed-${property.id}`;
  const [dismissed, setDismissed] = useState(() => {
    if (justPaid) return false; // always re-show the celebratory version
    try { return sessionStorage.getItem(key) === "1"; } catch { return false; }
  });
  if (dismissed) return null;
  const dismiss = () => {
    try { sessionStorage.setItem(key, "1"); } catch { /* noop */ }
    setDismissed(true);
  };
  return (
    <Card data-testid="paid-up-banner" className="rounded-2xl border-[#2C4C3B] bg-gradient-to-br from-[#2C4C3B] to-[#1F3D2E] text-white overflow-hidden relative mb-8">
      {/* subtle confetti dots */}
      <div className="absolute inset-0 opacity-20 pointer-events-none" aria-hidden="true">
        <div className="absolute top-6 left-8 w-2 h-2 rounded-full bg-[#EADCC8]" />
        <div className="absolute top-14 left-32 w-1.5 h-1.5 rounded-full bg-[#C86650]" />
        <div className="absolute top-8 right-20 w-2 h-2 rounded-full bg-[#EADCC8]" />
        <div className="absolute bottom-8 left-24 w-1.5 h-1.5 rounded-full bg-[#EADCC8]" />
        <div className="absolute top-20 right-40 w-2.5 h-2.5 rounded-full bg-[#C86650]" />
        <div className="absolute bottom-10 right-8 w-1.5 h-1.5 rounded-full bg-[#EADCC8]" />
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="paid-up-dismiss"
        className="absolute top-4 right-4 z-10 text-white/70 hover:text-white rounded-full p-1 hover:bg-white/10"
      >
        <X className="w-4 h-4" />
      </button>
      <CardContent className="p-8 md:p-10 relative flex items-center gap-6 flex-wrap">
        <div className="w-14 h-14 rounded-full bg-[#EADCC8]/20 flex items-center justify-center flex-shrink-0">
          <PartyPopper className="w-8 h-8 text-[#EADCC8]" />
        </div>
        <div className="flex-1 min-w-[16rem]">
          <p className="text-xs uppercase tracking-[0.3em] text-[#EADCC8] mb-1">{justPaid ? "Payment confirmed" : "Account current"}</p>
          <p className="font-serif text-2xl md:text-3xl">
            {justPaid ? "You're all paid up. Thank you!" : "You're all paid up."}
          </p>
          <p className="text-[#EADCC8] text-sm mt-2 max-w-lg">
            {justPaid
              ? "Your HOA dues are settled. A receipt has been sent to your email and the ledger is up to date."
              : `Your dues are current through ${new Date().getFullYear()}. Nothing owed to the HOA right now.`}
            {property.account_credit > 0 && (
              <span data-testid="banner-credit-note"> You have a <b>${property.account_credit.toFixed(2)}</b> account credit that will apply to next year's dues.</span>
            )}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/portal/history">
            <Button variant="outline" data-testid="paid-up-view-history" className="rounded-full bg-transparent border-[#EADCC8] text-[#EADCC8] hover:bg-[#EADCC8] hover:text-[#1F3D2E]">
              View payment history
            </Button>
          </Link>
          {justPaid && (
            <Link to="/portal/pay">
              <Button data-testid="paid-up-setup-autopay" className="rounded-full bg-[#C86650] hover:bg-[#B4553F] text-white">
                Set up auto-pay
              </Button>
            </Link>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


// ---------- My Home card (when linked) ----------
function MyHomeCard({ property, onUnlink }) {
  const balance = property.balance;
  const statusColor = property.status === "current" ? "bg-[#2C4C3B]" : property.status === "partial" ? "bg-[#EADCC8] text-[#2C4C3B]" : "bg-[#C86650]";
  const [disputeOpen, setDisputeOpen] = useState(false);
  const settlement = property.active_settlement;
  const settlementActive = settlement && settlement.option === "option1_65_paid_in_full" && settlement.status === "active";
  const [payingSettlement, setPayingSettlement] = useState(false);
  const paySettlement = async () => {
    setPayingSettlement(true);
    try {
      const { data } = await api.post("/payments/settlement-checkout", {
        property_id: property.id,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) window.location.href = data.checkout_url;
      else throw new Error("No checkout URL");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start checkout");
      setPayingSettlement(false);
    }
  };
  return (
    <Card data-testid="my-home-card" className="rounded-2xl border-[#D5CFC4] bg-[#EADCC8] mb-8">
      <CardContent className="p-8 grid md:grid-cols-3 gap-6 items-center">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-2">
            <Home className="w-5 h-5 text-[#2C4C3B]" />
            <p className="text-xs uppercase tracking-[0.3em] text-[#5C6A64]">Your Home</p>
          </div>
          <p className="font-serif text-2xl text-[#1A2421]">{property.address}</p>
          <p className="text-sm text-[#5C6A64] mt-1">Owner on record: {property.owner_name}</p>
          <button onClick={onUnlink} data-testid="unlink-home-btn" className="text-xs text-[#5C6A64] underline mt-3 hover:text-[#C86650]">
            Not your home? Unlink and search again
          </button>
        </div>
        <div className="text-right md:text-right border-l md:border-l border-[#D5CFC4] md:pl-6">
          <p className="text-xs uppercase tracking-wider text-[#5C6A64]">Balance Due</p>
          <p className={`font-serif text-4xl mt-1 ${balance > 0 ? "text-[#C86650]" : "text-[#2C4C3B]"}`}>
            ${balance.toFixed(2)}
          </p>
          {property.account_credit > 0 && (
            <div className="mt-2 inline-flex flex-col items-end px-2.5 py-1 rounded-md bg-[#2C4C3B]/10 border border-[#2C4C3B]/20" data-testid="account-credit-block">
              <p className="text-[10px] uppercase tracking-wider text-[#2C4C3B]">Account credit</p>
              <p className="text-sm font-medium text-[#2C4C3B]" data-testid="account-credit-amount">+${property.account_credit.toFixed(2)}</p>
              <p className="text-[10px] text-[#5C6A64] mt-0.5">Applied to next year's dues</p>
            </div>
          )}
          {property.carried_credit > 0 && (
            <div className="mt-2 inline-flex flex-col items-end px-2.5 py-1 rounded-md bg-[#EADCC8] border border-[#D5CFC4]" data-testid="carried-credit-block">
              <p className="text-[10px] uppercase tracking-wider text-[#5C6A64]">Credit from last year</p>
              <p className="text-sm font-medium text-[#2C4C3B]" data-testid="carried-credit-amount">–${property.carried_credit.toFixed(2)}</p>
              <p className="text-[10px] text-[#5C6A64] mt-0.5">Already reducing this year's dues</p>
            </div>
          )}
          <button
            data-testid="dispute-balance-link"
            onClick={() => setDisputeOpen(true)}
            className="mt-1 inline-flex items-center gap-1 text-xs text-[#5C6A64] hover:text-[#C86650] underline decoration-dotted"
          >
            <HelpCircle className="w-3 h-3" /> Question this balance?
          </button>
          <div className="flex items-center gap-2 justify-end mt-2">
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${statusColor} ${property.status === "current" ? "text-white" : property.status === "partial" ? "" : "text-white"}`}>
              {property.status}
            </span>
          </div>
          {balance > 0 && !settlementActive && (
            <div className="flex flex-col items-end gap-2 mt-4">
              <Link to="/portal/pay">
                <Button data-testid="my-home-pay-btn" className="rounded-full bg-[#C86650] hover:bg-[#B4553F] text-white">
                  Pay balance
                </Button>
              </Link>
              <Link to="/portal/pay">
                <Button
                  data-testid="my-home-arrangement-btn"
                  variant="outline"
                  className="rounded-full border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white"
                >
                  Set up arrangement
                </Button>
              </Link>
            </div>
          )}
        </div>
      </CardContent>
      {settlementActive && (
        <div
          data-testid="settlement-offer-banner"
          className="mx-6 mb-6 rounded-xl bg-white border border-[#C86650] p-5"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
              <p className="text-xs uppercase tracking-[0.3em] text-[#C86650] mb-1">Settlement offer active — Option 1</p>
              <p className="font-serif text-xl text-[#1A2421]">
                Pay <b>${Number(settlement.amount_due || 0).toFixed(2)}</b> to settle in full
                <span className="text-sm text-[#5C6A64] font-sans"> (65% reduction · save ${Number(settlement.discount_amount || 0).toFixed(2)})</span>
              </p>
              {settlement.deadline && (
                <p className="text-xs text-[#5C6A64] mt-1">
                  Must be paid by <b className="text-[#C86650]">{formatLocalDate(settlement.deadline, { month: "short", day: "numeric", year: "numeric" })}</b>, or the original balance of ${Number(settlement.original_balance || 0).toFixed(2)} is restored.
                </p>
              )}
            </div>
            <Button
              data-testid="pay-settlement-btn"
              onClick={paySettlement}
              disabled={payingSettlement}
              className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3D2E] text-white h-11 px-6 whitespace-nowrap"
            >
              {payingSettlement ? "Opening checkout…" : `Pay settlement · $${Number(settlement.amount_due || 0).toFixed(2)}`}
            </Button>
          </div>
        </div>
      )}
      <DisputeBalanceDialog open={disputeOpen} onOpenChange={setDisputeOpen} property={property} />
    </Card>
  );
}


// ---------- Dispute balance dialog ----------
function DisputeBalanceDialog({ open, onOpenChange, property }) {
  const REASONS = [
    { value: "payment_missing", label: "A payment I made isn't showing" },
    { value: "amount_higher", label: "The amount is higher than expected" },
    { value: "duplicate", label: "This looks like a duplicate or already-paid balance" },
    { value: "other", label: "Something else" },
  ];
  const [reason, setReason] = useState("payment_missing");
  const [details, setDetails] = useState("");
  const [preferredContact, setPreferredContact] = useState("email");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!details.trim()) return toast.error("Please describe what looks wrong");
    setBusy(true);
    try {
      await api.post("/portal/balance-dispute", {
        reason, details, preferred_contact: preferredContact, phone,
      });
      toast.success("Sent — the board will review within 2 business days");
      onOpenChange(false);
      setDetails(""); setPhone(""); setReason("payment_missing"); setPreferredContact("email");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dispute-balance-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Report a balance concern</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#5C6A64] -mt-2">We'll review within 2 business days and email you back.</p>

        {/* Auto-attached ledger snapshot */}
        <div className="rounded-xl bg-[#FDF9F1] border border-[#EADCC8] p-4" data-testid="dispute-snapshot">
          <p className="text-xs uppercase tracking-wider text-[#C86650] mb-2">Auto-attached — your current ledger</p>
          <p className="font-medium text-[#1A2421]">{property.address} · Unit {property.unit_number}</p>
          <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
            <p className="text-[#5C6A64]">Balance shown:</p><p className="font-serif text-[#C86650]">${(property.balance || 0).toFixed(2)}</p>
            <p className="text-[#5C6A64]">Annual assessment:</p><p className="text-[#1A2421]">${(property.annual_assessment || 0).toFixed(2)}</p>
            <p className="text-[#5C6A64]">Starting balance:</p><p className="text-[#1A2421]">${(property.starting_balance || 0).toFixed(2)}</p>
            <p className="text-[#5C6A64]">YTD paid:</p><p className="text-[#2C4C3B]">${(property.ytd_paid || 0).toFixed(2)}</p>
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C6A64]">What looks wrong?</label>
          <div className="mt-2 space-y-2" data-testid="dispute-reasons">
            {REASONS.map((r) => (
              <label key={r.value} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${reason === r.value ? "border-[#C86650] bg-[#FDF9F1]" : "border-[#EADCC8] hover:border-[#D5CFC4]"}`}>
                <input
                  type="radio"
                  name="dispute-reason"
                  value={r.value}
                  checked={reason === r.value}
                  onChange={() => setReason(r.value)}
                  data-testid={`dispute-reason-${r.value}`}
                  className="accent-[#C86650]"
                />
                <span className="text-sm text-[#1A2421]">{r.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Tell the board more</label>
          <Textarea
            data-testid="dispute-details"
            rows={4}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder="e.g. I paid $525 on Feb 3 (check #1234), but the ledger still shows the full balance…"
            className="mt-1 rounded-xl"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Preferred contact</label>
            <Select value={preferredContact} onValueChange={setPreferredContact}>
              <SelectTrigger data-testid="dispute-contact-select" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="phone">Phone call</SelectItem>
                <SelectItem value="text">Text message</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Phone (optional)</label>
            <Input data-testid="dispute-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 rounded-xl" placeholder="(555) 555-5555" />
          </div>
        </div>

        <Button
          data-testid="dispute-submit"
          onClick={submit}
          disabled={busy}
          className="w-full rounded-full bg-[#C86650] hover:bg-[#B4553F] text-white h-11"
        >
          {busy ? "Sending…" : "Send to the board"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Find my home (when no property linked) ----------
function FindMyHomeCard({ onLinked }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(null);

  const search = async (val) => {
    setQ(val);
    if (val.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get("/properties/search", { params: { q: val } });
      setResults(data);
    } catch (e) { console.error("Property search failed:", e); }
    setSearching(false);
  };

  const claim = async (id) => {
    setLinking(id);
    try {
      await api.post(`/properties/claim/${id}`);
      toast.success("Home linked to your account!");
      onLinked();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not link this home");
    } finally {
      setLinking(null);
    }
  };

  return (
    <Card data-testid="find-home-card" className="rounded-2xl border-2 border-dashed border-[#2C4C3B] bg-white mb-8">
      <CardContent className="p-8">
        <div className="flex items-center gap-2 mb-3">
          <Search className="w-5 h-5 text-[#C86650]" />
          <p className="text-xs uppercase tracking-[0.3em] text-[#C86650]">Set up your account</p>
        </div>
        <p className="font-serif text-2xl text-[#1A2421] mb-2">Connect your home</p>
        <p className="text-[#5C6A64] mb-6 max-w-xl">
          Search for your street address below to link this account to your property. Once linked, you'll see your dues balance, YTD payments, and can pay online in one click.
        </p>
        <Input
          data-testid="home-search-input"
          value={q}
          onChange={(e) => search(e.target.value)}
          placeholder="Start typing your address, e.g. 4856 W. Saddleridge…"
          className="rounded-xl h-12 max-w-lg"
        />
        {searching && <p className="text-sm text-[#5C6A64] mt-3">Searching…</p>}
        {results.length > 0 && (
          <div className="mt-4 border border-[#D5CFC4] rounded-xl overflow-hidden max-w-2xl">
            {results.map((r) => (
              <div key={r.id} data-testid={`home-result-${r.id}`} className="p-4 border-b border-[#D5CFC4] last:border-0 flex justify-between items-center gap-3">
                <div className="flex-1">
                  <p className="text-[#1A2421] font-medium">{r.address}</p>
                  <p className="text-xs text-[#5C6A64]">
                    Owner on record: {r.owner_name}
                    {r.is_claimed && <span className="ml-2 text-[#C86650]">· already linked</span>}
                  </p>
                </div>
                <Button
                  size="sm"
                  disabled={r.is_claimed || linking === r.id}
                  onClick={() => claim(r.id)}
                  data-testid={`claim-home-${r.id}`}
                  className="rounded-full bg-[#2C4C3B] hover:bg-[#233D2F] text-white"
                >
                  {linking === r.id ? "Linking…" : r.is_claimed ? "Linked" : (<><CheckCircle2 className="w-3 h-3 mr-1" /> This is mine</>)}
                </Button>
              </div>
            ))}
          </div>
        )}
        {q.length >= 2 && !searching && results.length === 0 && (
          <p className="text-sm text-[#5C6A64] mt-3">
            No match. Double-check the spelling or contact the board — <a href="mailto:saddleridgesavoytrailshoa@gmail.com" className="text-[#C86650] underline">saddleridgesavoytrailshoa@gmail.com</a>.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
